import { PublicationError } from '@streamlain/doc-publication-contract';
import { localAccountKeyError } from '@streamlain/view-config/local-data-seal';

import type { LocalAuthBinding } from '../storage/local-data-access';

export interface SearchIndexLease {
  readonly generation: number;
  readonly signal: AbortSignal;
}
export type SearchIndexSource =
  | { readonly kind: 'persistent' }
  | {
      readonly kind: 'account-ram';
      awaitReady(signal?: AbortSignal): Promise<SearchIndexLease>;
      assertCurrent(lease: SearchIndexLease): void;
      onInvalidate(listener: (error: Error) => void): () => void;
      invalidate(error: unknown): void;
      reconnect(): void;
    };
const owners = new Map<string, Set<AccountSearchIndexSource>>();
const bindingId = (binding: LocalAuthBinding) =>
  JSON.stringify([binding.accountId, binding.workspaceId, binding.authEpoch]);

/** Narrow lifecycle seam for the later Client/Worker/Lock consumer. No auth policy. */
export function invalidateAccountSearchIndexes(
  binding: LocalAuthBinding,
  error: unknown
) {
  for (const source of owners.get(bindingId(binding)) ?? [])
    source.invalidate(error);
}

/**
 * Another writer on the same local database advanced the index head. The
 * account authority is intact; only this instance's RAM copy is stale.
 */
export function isLocalIndexConflict(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'code' in error &&
    error.code === 'LOCAL_CONFLICT'
  );
}

export function searchIndexFailure(error: unknown): Error {
  if (error && typeof error === 'object' && 'code' in error) {
    switch (error.code) {
      case 'LOCKED':
      case 'KEY_UNAVAILABLE':
      case 'CORRUPT':
      case 'DENIED':
      case 'LOCAL_STORE_NEWER':
        return localAccountKeyError(error.code);
      case 'LOCAL_CONFLICT':
      case 'FORMAT_MISMATCH':
      case 'LOCAL_QUOTA':
      case 'CANCELLED':
        return new PublicationError(error.code);
    }
  }
  return localAccountKeyError('CORRUPT');
}

/** Invalidates output synchronously before dropping the owned RAM generation. */
export class AccountSearchIndexSource {
  readonly kind = 'account-ram';
  private generation = 0;
  private controller = new AbortController();
  private ready: Promise<void> | null = null;
  private failure: Error | null = null;
  private readonly listeners = new Set<(error: Error) => void>();
  constructor(
    readonly binding: LocalAuthBinding,
    private readonly load: (signal: AbortSignal) => Promise<void>,
    private readonly check: () => Promise<void>,
    /** The account lock in memory, read without a transaction. */
    private readonly authorized: () => boolean,
    private readonly drop: () => void
  ) {
    this.register();
  }
  private register() {
    const id = bindingId(this.binding);
    const set = owners.get(id) ?? new Set<AccountSearchIndexSource>();
    set.add(this);
    owners.set(id, set);
  }
  private unregister() {
    const id = bindingId(this.binding);
    const set = owners.get(id);
    set?.delete(this);
    if (!set?.size) owners.delete(id);
  }
  /** Explicit handle reconnect starts a fresh rebuild, never an authority retry. */
  reconnect() {
    this.invalidate(localAccountKeyError('LOCKED'));
    this.generation++;
    this.controller = new AbortController();
    this.ready = null;
    this.failure = null;
    this.register();
  }
  invalidate(error: unknown) {
    if (this.failure) return;
    const failure = searchIndexFailure(error);
    this.failure = failure;
    this.controller.abort(failure);
    this.unregister();
    this.drop();
    for (const listener of this.listeners) listener(failure);
  }
  onInvalidate(listener: (error: Error) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  /** Every output: the lease is current and the account did not lock since
   * the last index read, so no result of a logged-out login is delivered. */
  assertCurrent(lease: SearchIndexLease) {
    if (this.failure) throw this.failure;
    if (!this.authorized()) {
      this.invalidate(localAccountKeyError('LOCKED'));
      throw this.failure!;
    }
    if (
      lease.generation !== this.generation ||
      lease.signal !== this.controller.signal ||
      lease.signal.aborted
    )
      throw localAccountKeyError('LOCKED');
  }
  private async assertAuthority(lease: SearchIndexLease) {
    this.assertCurrent(lease);
    try {
      await this.check();
      this.assertCurrent(lease);
    } catch (error) {
      if (lease.generation === this.generation) this.invalidate(error);
      throw searchIndexFailure(error);
    }
  }
  async awaitReady(signal?: AbortSignal): Promise<SearchIndexLease> {
    const lease = {
      generation: this.generation,
      signal: this.controller.signal,
    };
    this.assertCurrent(lease);
    if (!this.ready) {
      this.ready = (async () => {
        await this.load(lease.signal);
        await this.assertAuthority(lease);
      })().catch(error => {
        if (lease.generation === this.generation) this.invalidate(error);
        throw searchIndexFailure(error);
      });
      // Ownership is retained even if every caller cancels its individual wait.
      void this.ready.catch(() => {});
    }
    await new Promise<void>((resolve, reject) => {
      const combined = signal
        ? AbortSignal.any([signal, lease.signal])
        : lease.signal;
      const aborted = () => {
        combined.removeEventListener('abort', aborted);
        reject(combined.reason);
      };
      combined.addEventListener('abort', aborted, { once: true });
      if (combined.aborted) {
        aborted();
        return;
      }
      this.ready?.then(
        () => {
          combined.removeEventListener('abort', aborted);
          resolve();
        },
        error => {
          combined.removeEventListener('abort', aborted);
          reject(error);
        }
      );
    });
    // Authority was checked when the index loaded; every read checks it
    // again around its work (EncryptedIndexSegments).
    this.assertCurrent(lease);
    return lease;
  }
}
